// src/pages/DancersList.js - dancers of the leader's group.
//
// Audit fixes: FL-14 (works on phones), FL-15 (bordered inputs), FL-16 (no double submit,
// birth date limits, errors inside the dialog, "save and add another"), FL-17 (import
// result message), FL-27 (signature sheet download), FL-32 (search, sort, count, loading
// and empty states), FL-29 (dialogs instead of window.confirm).
// Season 2027:
// * 2.2 dancers from past seasons can be removed from the list ("Odebrat ze seznamu");
//   they stay in their old choreographies and can be brought back (tab "Odebraní");
// * 2.3 the same person cannot be added twice (the server refuses it); a tab lists
//   probable duplicates with a merge;
// * 2.4 search by first name / last name / birth date and filter by age.
// "Vrátit do seznamu" in the add dialog shows its error inside the dialog and is busy meanwhile.
import React, { useCallback, useMemo, useRef, useState } from 'react';
import { Archive, Copy, FileSignature, Lock, Plus, RotateCcw, Search, Trash2, Upload, Users } from 'lucide-react';
import api, { dancersApi, getErrorMessage } from '../services/api';
import useFetch from '../hooks/useFetch';
import { formatDate, countLabel } from '../utils/format';
import { blobErrorMessage, filenameFromHeaders, saveBlob } from '../utils/download';
import { Alert, Button, ConfirmDialog, EmptyState, Modal, PageHeader, Spinner, useToast } from '../components/ui';
import DancersImportModal from '../components/layout/DancersImportModal';
import TextField from '../components/leader/TextField';
import LoadError from '../components/leader/LoadError';
import { yearsLabel } from '../components/leader/DancerPicker';
import { useMeta } from '../contexts/MetaContext';

const DANCER_FORMS = ['tanečník', 'tanečníci', 'tanečníků'];
// "Odebrat 1 tanečníka / 3 tanečníky / 5 tanečníků"
const DANCER_ACCUSATIVE_FORMS = ['tanečníka', 'tanečníky', 'tanečníků'];
const EMPTY_DANCER = { firstName: '', lastName: '', birthDate: '' };
const MIN_BIRTH_DATE = '1920-01-01';

const todayIso = () => {
  const d = new Date();
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
};

const normalise = (text) =>
  String(text || '')
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .trim()
    .toLowerCase();

const SORTS = {
  lastName: {
    label: 'Příjmení (A–Z)',
    compare: (a, b) => a.lastName.localeCompare(b.lastName, 'cs') || a.firstName.localeCompare(b.firstName, 'cs'),
  },
  firstName: {
    label: 'Jméno (A–Z)',
    compare: (a, b) => a.firstName.localeCompare(b.firstName, 'cs') || a.lastName.localeCompare(b.lastName, 'cs'),
  },
  youngest: { label: 'Od nejmladších', compare: (a, b) => (b.birthDate || '').localeCompare(a.birthDate || '') },
  oldest: { label: 'Od nejstarších', compare: (a, b) => (a.birthDate || '').localeCompare(b.birthDate || '') },
};

const SEARCH_FIELDS = {
  all: 'Vše',
  firstName: 'Jméno',
  lastName: 'Příjmení',
  birthDate: 'Datum narození',
};

/** "29. 9. 1995", "29.9.1995", "1995-09-29", "1995" or "9.1995" against an ISO date. */
function birthDateMatches(iso, query) {
  if (!iso) return false;
  const q = query.replace(/\s+/g, '');
  if (!q) return true;
  const [y, m, d] = iso.split('-').map(Number);
  const variants = [iso, `${d}.${m}.${y}`, `${String(d).padStart(2, '0')}.${String(m).padStart(2, '0')}.${y}`];
  return variants.some((v) => v.includes(q)) || `${m}.${y}`.includes(q);
}

function matches(dancer, query, field) {
  const q = normalise(query);
  if (!q) return true;
  if (field === 'firstName') return normalise(dancer.firstName).includes(q);
  if (field === 'lastName') return normalise(dancer.lastName).includes(q);
  if (field === 'birthDate') return birthDateMatches(dancer.birthDate, query.trim());
  return (
    normalise(`${dancer.firstName} ${dancer.lastName}`).includes(q) ||
    normalise(`${dancer.lastName} ${dancer.firstName}`).includes(q) ||
    birthDateMatches(dancer.birthDate, query.trim())
  );
}

const ageIn = (dancer, year) => year - Number(String(dancer.birthDate || '').slice(0, 4));

function LastCompetition({ dancer }) {
  if (!dancer.lastCompetition) return <span className="text-gray-400">–</span>;
  return (
    <span title={formatDate(dancer.lastCompetition.date)}>
      {dancer.lastCompetition.name}
      <span className="block text-xs text-gray-500">{formatDate(dancer.lastCompetition.date)}</span>
    </span>
  );
}

const DancersList = () => {
  const toast = useToast();
  const meta = useMeta();
  const archiveEnabled = meta.featureEnabled('dancer_archive');
  const season = meta.currentSeason || new Date().getFullYear();

  const [tab, setTab] = useState('active'); // active | archived | duplicates
  const { data, error, isLoading, reload } = useFetch(() => dancersApi.getAll().then((res) => res.data || []), 'all');
  const archivedFetch = useFetch(
    () => (archiveEnabled ? dancersApi.getArchived().then((res) => res.data || []) : Promise.resolve([])),
    `archived-${archiveEnabled}`
  );
  const duplicatesFetch = useFetch(
    () => (archiveEnabled ? dancersApi.duplicates().then((res) => res.data || []) : Promise.resolve([])),
    `duplicates-${archiveEnabled}`
  );
  const dancers = useMemo(() => data || [], [data]);
  const archived = useMemo(() => archivedFetch.data || [], [archivedFetch.data]);
  const duplicateSets = useMemo(() => duplicatesFetch.data || [], [duplicatesFetch.data]);

  const [query, setQuery] = useState('');
  const [searchField, setSearchField] = useState('all');
  const [ageFrom, setAgeFrom] = useState('');
  const [ageTo, setAgeTo] = useState('');
  const [sort, setSort] = useState('lastName');
  const [pageError, setPageError] = useState(null);
  const [selected, setSelected] = useState(() => new Set());

  const [isAddOpen, setIsAddOpen] = useState(false);
  const [newDancer, setNewDancer] = useState(EMPTY_DANCER);
  const [addError, setAddError] = useState(null);
  const [addConflict, setAddConflict] = useState(null); // {code, existing, message}
  const [isSaving, setIsSaving] = useState(false);
  const [isRestoring, setIsRestoring] = useState(false);
  const savingRef = useRef(false);
  const firstNameRef = useRef(null);

  const [isImportOpen, setIsImportOpen] = useState(false);
  const [toDelete, setToDelete] = useState(null);
  const [deleteError, setDeleteError] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [toArchive, setToArchive] = useState(null); // list of dancers
  const [archiveBusy, setArchiveBusy] = useState(false);
  const [archiveError, setArchiveError] = useState(null);
  const [mergeState, setMergeState] = useState(null); // {set, keepId}
  const [mergeBusy, setMergeBusy] = useState(false);
  const [mergeError, setMergeError] = useState(null);
  const [isDownloadingSheet, setIsDownloadingSheet] = useState(false);

  const reloadArchived = archivedFetch.reload;
  const reloadDuplicates = duplicatesFetch.reload;
  const reloadAll = useCallback(() => {
    reload({ silent: true });
    reloadArchived({ silent: true });
    reloadDuplicates({ silent: true });
  }, [reload, reloadArchived, reloadDuplicates]);

  const source = tab === 'archived' ? archived : dancers;
  const visible = useMemo(() => {
    const from = ageFrom === '' ? null : Number(ageFrom);
    const to = ageTo === '' ? null : Number(ageTo);
    return source
      .filter((d) => matches(d, query, searchField))
      .filter((d) => {
        const age = ageIn(d, season);
        return (from === null || age >= from) && (to === null || age <= to);
      })
      .sort(SORTS[sort].compare);
  }, [source, query, searchField, ageFrom, ageTo, sort, season]);

  const filtersActive = query.trim() !== '' || ageFrom !== '' || ageTo !== '';
  const archivable = (d) => !d.isLocked;

  const openAdd = () => {
    setNewDancer(EMPTY_DANCER);
    setAddError(null);
    setAddConflict(null);
    setIsAddOpen(true);
  };

  const closeAdd = useCallback(() => {
    if (savingRef.current) return;
    setIsAddOpen(false);
  }, []);

  const closeImport = useCallback(() => setIsImportOpen(false), []);
  const closeDelete = useCallback(() => {
    setToDelete(null);
    setDeleteError(null);
  }, []);

  const saveDancer = async (keepOpen, force = false) => {
    if (savingRef.current) return;
    const payload = {
      firstName: newDancer.firstName.trim(),
      lastName: newDancer.lastName.trim(),
      birthDate: newDancer.birthDate,
    };
    if (!payload.firstName || !payload.lastName || !payload.birthDate) {
      setAddError('Vyplňte prosím jméno, příjmení i datum narození.');
      return;
    }
    if (payload.birthDate > todayIso() || payload.birthDate < MIN_BIRTH_DATE) {
      setAddError('Zkontrolujte prosím datum narození.');
      return;
    }
    savingRef.current = true;
    setIsSaving(true);
    setAddError(null);
    setAddConflict(null);
    try {
      await dancersApi.create(force ? { ...payload, force: true } : payload);
      toast.success(`Tanečník ${payload.firstName} ${payload.lastName} byl přidán.`);
      setNewDancer(EMPTY_DANCER);
      if (keepOpen) {
        if (firstNameRef.current) firstNameRef.current.focus();
      } else {
        setIsAddOpen(false);
      }
      reloadAll();
    } catch (err) {
      const body = (err && err.data) || {};
      if (err?.status === 409 && (body.code === 'duplicate' || body.code === 'possible_duplicate')) {
        setAddConflict({ code: body.code, existing: body.existing, message: getErrorMessage(err) });
      } else {
        setAddError(getErrorMessage(err));
      }
    } finally {
      savingRef.current = false;
      setIsSaving(false);
    }
  };

  const restoreDancer = async (dancer) => {
    try {
      await dancersApi.restore(dancer.id);
      toast.success(`${dancer.firstName} ${dancer.lastName} je zpět v seznamu.`);
      setIsAddOpen(false);
      reloadAll();
    } catch (err) {
      setPageError(getErrorMessage(err));
    }
  };

  // "Vrátit do seznamu" offered in the add dialog for an archived duplicate: the dialog stays
  // open (and cannot be closed) while it runs and shows a failure inside.
  const restoreFromDialog = async (dancer) => {
    if (savingRef.current) return;
    savingRef.current = true;
    setIsRestoring(true);
    setAddError(null);
    try {
      await dancersApi.restore(dancer.id);
      toast.success(`${dancer.firstName} ${dancer.lastName} je zpět v seznamu.`);
      setAddConflict(null);
      setIsAddOpen(false);
      reloadAll();
    } catch (err) {
      setAddError(getErrorMessage(err));
    } finally {
      savingRef.current = false;
      setIsRestoring(false);
    }
  };

  const handleAddSubmit = (e) => {
    e.preventDefault();
    saveDancer(false);
  };

  const handleDelete = async () => {
    if (!toDelete) return;
    setIsDeleting(true);
    setDeleteError(null);
    try {
      await dancersApi.delete(toDelete.id);
      toast.success(`Tanečník ${toDelete.firstName} ${toDelete.lastName} byl smazán.`);
      setToDelete(null);
      reloadAll();
    } catch (err) {
      setDeleteError(getErrorMessage(err));
    } finally {
      setIsDeleting(false);
    }
  };

  const handleArchive = async () => {
    if (!toArchive) return;
    setArchiveBusy(true);
    setArchiveError(null);
    try {
      const { data: result } = await dancersApi.archive(toArchive.map((d) => d.id));
      if (result.archived.length === 0) {
        setArchiveError(result.message);
        return;
      }
      toast.success(result.message);
      setToArchive(null);
      setSelected(new Set());
      reloadAll();
    } catch (err) {
      setArchiveError(getErrorMessage(err));
    } finally {
      setArchiveBusy(false);
    }
  };

  const handleMerge = async () => {
    if (!mergeState) return;
    setMergeBusy(true);
    setMergeError(null);
    try {
      const others = mergeState.set.filter((d) => d.id !== mergeState.keepId).map((d) => d.id);
      const { data: result } = await dancersApi.merge(mergeState.keepId, others);
      toast.success(result.message || 'Duplicity byly sloučeny.');
      setMergeState(null);
      reloadAll();
    } catch (err) {
      setMergeError(getErrorMessage(err));
    } finally {
      setMergeBusy(false);
    }
  };

  const handleImported = (result) => {
    setIsImportOpen(false);
    toast.success((result && result.message) || 'Tanečníci byli importováni.');
    reloadAll();
  };

  const handleDownloadSignatureSheet = async () => {
    setIsDownloadingSheet(true);
    setPageError(null);
    try {
      const response = await api.get('/dancers/signature-sheet', { responseType: 'blob' });
      const filename = filenameFromHeaders(response.headers, 'Podpisovy_arch.pdf');
      saveBlob(new Blob([response.data], { type: 'application/pdf' }), filename);
    } catch (err) {
      setPageError(await blobErrorMessage(err, 'Podpisový arch se nepodařilo stáhnout. Zkuste to prosím znovu.'));
    } finally {
      setIsDownloadingSheet(false);
    }
  };

  const toggleSelected = (id) =>
    setSelected((cur) => {
      const next = new Set(cur);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  if (isLoading && !data) return <Spinner label="Načítám tanečníky…" />;
  if (error && !data) return <LoadError title="Seznam tanečníků se nepodařilo načíst" error={error} onRetry={reload} />;

  const selectableVisible = visible.filter(archivable);
  const allVisibleSelected = selectableVisible.length > 0 && selectableVisible.every((d) => selected.has(d.id));
  const selectedDancers = dancers.filter((d) => selected.has(d.id));

  const tabButton = (key, label, count) => (
    <button
      key={key}
      type="button"
      role="tab"
      aria-selected={tab === key}
      onClick={() => {
        setTab(key);
        setSelected(new Set());
      }}
      className={`-mb-px border-b-2 px-3 py-2 text-sm font-medium focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 ${
        tab === key ? 'border-indigo-600 text-indigo-700' : 'border-transparent text-gray-600 hover:text-gray-900'
      }`}
    >
      {label} <span className="text-gray-500">({count})</span>
    </button>
  );

  const filterBar = (
    <div className="flex flex-col gap-3 border-b border-gray-200 p-4 lg:flex-row lg:items-end lg:justify-between">
      <div className="flex flex-col gap-2 sm:flex-row sm:flex-wrap sm:items-end">
        <label className="text-sm text-gray-700">
          <span className="mb-1 block">Hledat v</span>
          <select
            value={searchField}
            onChange={(e) => setSearchField(e.target.value)}
            className="w-full rounded-md border border-gray-300 px-2 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 sm:w-auto"
          >
            {Object.entries(SEARCH_FIELDS).map(([key, label]) => (
              <option key={key} value={key}>
                {label}
              </option>
            ))}
          </select>
        </label>
        <label className="relative block w-full sm:w-64">
          <span className="sr-only">Hledat tanečníka</span>
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" aria-hidden="true" />
          <input
            type="search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={searchField === 'birthDate' ? 'např. 29.9.2012 nebo 2012' : 'Hledat…'}
            className="block w-full rounded-md border border-gray-300 py-2 pl-9 pr-3 text-base focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500 sm:text-sm"
          />
        </label>
        <fieldset className="text-sm text-gray-700">
          <legend className="mb-1">Věk v roce {season}</legend>
          <div className="flex items-center gap-1">
            <input
              type="number"
              min="0"
              max="99"
              inputMode="numeric"
              value={ageFrom}
              onChange={(e) => setAgeFrom(e.target.value)}
              aria-label="Věk od"
              placeholder="od"
              className="w-16 rounded-md border border-gray-300 px-2 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
            />
            <span aria-hidden="true">–</span>
            <input
              type="number"
              min="0"
              max="99"
              inputMode="numeric"
              value={ageTo}
              onChange={(e) => setAgeTo(e.target.value)}
              aria-label="Věk do"
              placeholder="do"
              className="w-16 rounded-md border border-gray-300 px-2 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
            />
          </div>
        </fieldset>
        {filtersActive && (
          <Button
            variant="ghost"
            onClick={() => {
              setQuery('');
              setAgeFrom('');
              setAgeTo('');
            }}
          >
            Zrušit filtry
          </Button>
        )}
      </div>
      <label className="flex items-center gap-2 text-sm text-gray-700">
        Řadit
        <select
          value={sort}
          onChange={(e) => setSort(e.target.value)}
          className="rounded-md border border-gray-300 px-2 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
        >
          {Object.entries(SORTS).map(([key, s]) => (
            <option key={key} value={key}>
              {s.label}
            </option>
          ))}
        </select>
      </label>
    </div>
  );

  const renderActions = (dancer) => {
    if (tab === 'archived') {
      return (
        <Button variant="secondary" onClick={() => restoreDancer(dancer)}>
          <RotateCcw className="h-4 w-4" aria-hidden="true" />
          Vrátit
        </Button>
      );
    }
    if (!dancer.isUsedInChoreography) {
      return (
        <button
          type="button"
          onClick={() => setToDelete(dancer)}
          aria-label={`Smazat tanečníka ${dancer.firstName} ${dancer.lastName}`}
          title="Smazat tanečníka (ještě netančil v žádné choreografii)"
          className="rounded-md p-2 text-gray-500 hover:bg-red-50 hover:text-red-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500"
        >
          <Trash2 className="h-5 w-5" aria-hidden="true" />
        </button>
      );
    }
    if (!archiveEnabled) {
      return (
        <button
          type="button"
          disabled
          aria-label={`${dancer.firstName} ${dancer.lastName} nelze smazat – je v choreografii`}
          title="Tanečníka v choreografii nelze smazat. Nejdřív ho odeberte ze všech choreografií."
          className="rounded-md p-2 text-gray-500 disabled:cursor-not-allowed disabled:opacity-40"
        >
          <Trash2 className="h-5 w-5" aria-hidden="true" />
        </button>
      );
    }
    return (
      <button
        type="button"
        onClick={() => setToArchive([dancer])}
        disabled={dancer.isLocked}
        aria-label={`Odebrat ${dancer.firstName} ${dancer.lastName} ze seznamu`}
        title={
          dancer.isLocked
            ? 'Tančí v choreografii sezóny, která ještě neskončila – odebrat půjde po její poslední soutěži (Grand Finále).'
            : 'Odebrat ze seznamu (v historii choreografií zůstane)'
        }
        className="rounded-md p-2 text-gray-500 hover:bg-amber-50 hover:text-amber-800 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:bg-transparent"
      >
        <Archive className="h-5 w-5" aria-hidden="true" />
      </button>
    );
  };

  const renderTable = () => (
    <div className="overflow-x-auto">
      <table className="min-w-full divide-y divide-gray-200 text-sm">
        <thead className="bg-gray-50">
          <tr>
            {tab === 'active' && archiveEnabled && (
              <th scope="col" className="w-10 px-4 py-3">
                <input
                  type="checkbox"
                  aria-label="Vybrat všechny zobrazené tanečníky, které lze odebrat"
                  checked={allVisibleSelected}
                  onChange={() => setSelected(allVisibleSelected ? new Set() : new Set(selectableVisible.map((d) => d.id)))}
                  className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                />
              </th>
            )}
            <th scope="col" className="px-4 py-3 text-left font-medium text-gray-700">
              Tanečník
            </th>
            <th scope="col" className="hidden px-4 py-3 text-left font-medium text-gray-700 sm:table-cell">
              Datum narození
            </th>
            <th scope="col" className="hidden px-4 py-3 text-left font-medium text-gray-700 sm:table-cell">
              Věk v roce {season}
            </th>
            {archiveEnabled && (
              <th scope="col" className="hidden px-4 py-3 text-left font-medium text-gray-700 md:table-cell">
                Naposledy v soutěži
              </th>
            )}
            <th scope="col" className="px-4 py-3 text-right font-medium text-gray-700">
              <span className="sr-only">Akce</span>
            </th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100">
          {visible.map((dancer) => (
            <tr key={dancer.id} className={selected.has(dancer.id) ? 'bg-indigo-50/50' : undefined}>
              {tab === 'active' && archiveEnabled && (
                <td className="px-4 py-3">
                  <input
                    type="checkbox"
                    disabled={!archivable(dancer)}
                    checked={selected.has(dancer.id)}
                    onChange={() => toggleSelected(dancer.id)}
                    aria-label={`Vybrat ${dancer.firstName} ${dancer.lastName}`}
                    className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500 disabled:opacity-40"
                  />
                </td>
              )}
              <td className="px-4 py-3">
                <p className="font-medium text-gray-900">
                  {dancer.lastName} {dancer.firstName}
                </p>
                <p className="text-gray-600 sm:hidden">
                  {formatDate(dancer.birthDate)} · {yearsLabel(ageIn(dancer, season))}
                </p>
                {dancer.isLocked && (
                  <p className="inline-flex items-center gap-1 text-xs text-gray-600">
                    <Lock className="h-3 w-3" aria-hidden="true" />
                    Tančí v probíhající sezóně
                  </p>
                )}
                {!dancer.isLocked && dancer.isUsedInChoreography && !archiveEnabled && (
                  <p className="text-xs text-gray-600">V choreografii</p>
                )}
              </td>
              <td className="hidden whitespace-nowrap px-4 py-3 text-gray-700 sm:table-cell">{formatDate(dancer.birthDate)}</td>
              <td className="hidden whitespace-nowrap px-4 py-3 text-gray-700 sm:table-cell">
                {yearsLabel(ageIn(dancer, season))}
              </td>
              {archiveEnabled && (
                <td className="hidden px-4 py-3 text-gray-700 md:table-cell">
                  <LastCompetition dancer={dancer} />
                </td>
              )}
              <td className="whitespace-nowrap px-4 py-3 text-right">{renderActions(dancer)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );

  const renderDuplicates = () =>
    duplicateSets.length === 0 ? (
      <p className="p-6 text-center text-sm text-gray-600">V seznamu nejsou žádné duplicity.</p>
    ) : (
      <div className="space-y-4 p-4">
        <p className="text-sm text-gray-600">
          Tyto záznamy mají stejné datum narození a stejné nebo velmi podobné jméno. Vyberte záznam, který chcete ponechat –
          choreografie ostatních se k němu převedou a duplicitní záznamy zmizí.
        </p>
        {duplicateSets.map((set) => (
          <div key={set.map((d) => d.id).join('-')} className="rounded-lg border border-gray-200 p-3">
            <ul className="divide-y divide-gray-100">
              {set.map((d) => (
                <li key={d.id} className="flex flex-wrap items-center justify-between gap-2 py-2 text-sm">
                  <span>
                    <span className="font-medium text-gray-900">
                      {d.lastName} {d.firstName}
                    </span>{' '}
                    <span className="text-gray-600">· {formatDate(d.birthDate)}</span>
                    {d.archivedAt && <span className="ml-2 rounded bg-gray-100 px-1.5 py-0.5 text-xs text-gray-600">odebraný</span>}
                    <span className="block text-xs text-gray-500">
                      {d.choreographyCount
                        ? `${countLabel(d.choreographyCount, ['choreografie', 'choreografie', 'choreografií'])}, naposledy ${
                            d.lastCompetition?.name || ''
                          }`
                        : 'zatím v žádné choreografii'}
                    </span>
                  </span>
                  <Button variant="secondary" onClick={() => setMergeState({ set, keepId: d.id })}>
                    <Copy className="h-4 w-4" aria-hidden="true" />
                    Ponechat tento
                  </Button>
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>
    );

  return (
    <div>
      <PageHeader
        title="Tanečníci"
        subtitle={dancers.length > 0 ? countLabel(dancers.length, DANCER_FORMS) : undefined}
        actions={
          <>
            <Button onClick={openAdd} className="w-full sm:w-auto">
              <Plus className="h-4 w-4" aria-hidden="true" />
              Přidat tanečníka
            </Button>
            <Button variant="secondary" onClick={() => setIsImportOpen(true)} className="w-full sm:w-auto">
              <Upload className="h-4 w-4" aria-hidden="true" />
              Import z Excelu
            </Button>
            <Button
              variant="secondary"
              onClick={handleDownloadSignatureSheet}
              loading={isDownloadingSheet}
              className="w-full sm:w-auto"
            >
              <FileSignature className="h-4 w-4" aria-hidden="true" />
              {isDownloadingSheet ? 'Připravuji…' : 'Podpisový arch'}
            </Button>
          </>
        }
      >
        <p className="mt-2 max-w-2xl text-sm text-gray-600">
          Podpisový arch (PDF) obsahuje všechny tanečníky z vašich choreografií na soutěže, které teprve budou. Vytiskněte ho a nechte podepsat na
          soutěži.
        </p>
      </PageHeader>

      {pageError && (
        <Alert tone="error" className="mb-4" onClose={() => setPageError(null)}>
          {pageError}
        </Alert>
      )}

      {dancers.length === 0 && archived.length === 0 ? (
        <EmptyState
          icon={Users}
          title="Zatím nemáte žádné tanečníky"
          action={
            <>
              <Button onClick={openAdd}>
                <Plus className="h-4 w-4" aria-hidden="true" />
                Přidat tanečníka
              </Button>
              <Button variant="secondary" onClick={() => setIsImportOpen(true)}>
                <Upload className="h-4 w-4" aria-hidden="true" />
                Import z Excelu
              </Button>
            </>
          }
        >
          Přidejte je jednotlivě, nebo hromadně ze souboru Excel.
        </EmptyState>
      ) : (
        <div className="rounded-lg bg-white shadow">
          {archiveEnabled && (
            <div role="tablist" aria-label="Seznam tanečníků" className="flex gap-2 border-b border-gray-200 px-4 pt-2">
              {tabButton('active', 'Aktivní', dancers.length)}
              {tabButton('archived', 'Odebraní', archived.length)}
              {duplicateSets.length > 0 && tabButton('duplicates', 'Možné duplicity', duplicateSets.length)}
            </div>
          )}

          {tab === 'duplicates' ? (
            renderDuplicates()
          ) : (
            <>
              {filterBar}
              {tab === 'active' && archiveEnabled && selected.size > 0 && (
                <div className="flex flex-wrap items-center justify-between gap-2 border-b border-indigo-100 bg-indigo-50 px-4 py-2 text-sm">
                  <span>Vybráno: {countLabel(selected.size, DANCER_FORMS)}</span>
                  <Button variant="secondary" onClick={() => setToArchive(selectedDancers)}>
                    <Archive className="h-4 w-4" aria-hidden="true" />
                    Odebrat ze seznamu
                  </Button>
                </div>
              )}
              {visible.length === 0 ? (
                <p className="p-6 text-center text-sm text-gray-600">
                  {tab === 'archived' && archived.length === 0 ? 'Žádní odebraní tanečníci.' : 'Žádný tanečník neodpovídá hledání.'}
                </p>
              ) : (
                renderTable()
              )}
              {tab === 'active' && (
                <p className="border-t border-gray-100 px-4 py-3 text-xs text-gray-600">
                  {archiveEnabled
                    ? 'Tanečníky z minulých sezón můžete odebrat ze seznamu – v jejich choreografiích zůstanou a kdykoli je vrátíte v záložce Odebraní. Tanečníky přihlášené v sezóně, která ještě neskončila, půjde odebrat po její poslední soutěži.'
                    : 'Tanečníky zařazené v choreografii nelze smazat – nejdřív je odeberte ze všech choreografií.'}
                </p>
              )}
            </>
          )}
        </div>
      )}

      {isAddOpen && (
        <Modal
          title="Přidat tanečníka"
          onClose={closeAdd}
          size="sm"
          initialFocusRef={firstNameRef}
          footer={
            <>
              <Button variant="secondary" onClick={closeAdd} disabled={isSaving || isRestoring}>
                Zrušit
              </Button>
              <Button variant="secondary" onClick={() => saveDancer(true)} disabled={isSaving || isRestoring}>
                Uložit a přidat dalšího
              </Button>
              <Button type="submit" form="add-dancer-form" loading={isSaving} disabled={isRestoring}>
                Uložit
              </Button>
            </>
          }
        >
          <form id="add-dancer-form" onSubmit={handleAddSubmit} className="space-y-4" noValidate>
            {addError && <Alert tone="error">{addError}</Alert>}
            <TextField
              ref={firstNameRef}
              label="Jméno"
              value={newDancer.firstName}
              onChange={(e) => setNewDancer({ ...newDancer, firstName: e.target.value })}
              maxLength={50}
              autoComplete="off"
              required
            />
            <TextField
              label="Příjmení"
              value={newDancer.lastName}
              onChange={(e) => setNewDancer({ ...newDancer, lastName: e.target.value })}
              maxLength={50}
              autoComplete="off"
              required
            />
            <TextField
              label="Datum narození"
              type="date"
              value={newDancer.birthDate}
              onChange={(e) => setNewDancer({ ...newDancer, birthDate: e.target.value })}
              min={MIN_BIRTH_DATE}
              max={todayIso()}
              required
            />
            {addConflict && (
              <Alert tone={addConflict.code === 'duplicate' ? 'error' : 'warning'}>
                <p>{addConflict.message}</p>
                <div className="mt-2 flex flex-wrap gap-2">
                  {addConflict.existing?.archived && (
                    <Button
                      variant="secondary"
                      onClick={() => restoreFromDialog(addConflict.existing)}
                      loading={isRestoring}
                      disabled={isSaving}
                    >
                      <RotateCcw className="h-4 w-4" aria-hidden="true" />
                      {isRestoring ? 'Vracím…' : 'Vrátit do seznamu'}
                    </Button>
                  )}
                  {addConflict.code === 'possible_duplicate' && (
                    <Button variant="secondary" onClick={() => saveDancer(false, true)} disabled={isSaving || isRestoring}>
                      Je to jiný tanečník – přidat
                    </Button>
                  )}
                </div>
              </Alert>
            )}
          </form>
        </Modal>
      )}

      {isImportOpen && <DancersImportModal onClose={closeImport} onConfirm={handleImported} existingDancers={dancers} />}

      {toDelete && (
        <ConfirmDialog
          title="Smazat tanečníka?"
          message={`Opravdu chcete smazat tanečníka ${toDelete.firstName} ${toDelete.lastName}?`}
          confirmLabel="Smazat"
          danger
          loading={isDeleting}
          error={deleteError}
          onConfirm={handleDelete}
          onCancel={closeDelete}
        />
      )}

      {toArchive && (
        <ConfirmDialog
          title={
            toArchive.length === 1
              ? 'Odebrat tanečníka ze seznamu?'
              : `Odebrat ${countLabel(toArchive.length, DANCER_ACCUSATIVE_FORMS)} ze seznamu?`
          }
          confirmLabel="Odebrat ze seznamu"
          loading={archiveBusy}
          error={archiveError}
          onConfirm={handleArchive}
          onCancel={() => {
            setToArchive(null);
            setArchiveError(null);
          }}
        >
          <p>
            {toArchive.length === 1
              ? `${toArchive[0].firstName} ${toArchive[0].lastName} zmizí z aktivního seznamu.`
              : 'Vybraní tanečníci zmizí z aktivního seznamu.'}{' '}
            V choreografiích minulých soutěží zůstanou a kdykoli je vrátíte v záložce Odebraní.
          </p>
        </ConfirmDialog>
      )}

      {mergeState && (
        <ConfirmDialog
          title="Sloučit duplicitní záznamy?"
          confirmLabel="Sloučit"
          loading={mergeBusy}
          error={mergeError}
          onConfirm={handleMerge}
          onCancel={() => {
            setMergeState(null);
            setMergeError(null);
          }}
        >
          {(() => {
            const keep = mergeState.set.find((d) => d.id === mergeState.keepId);
            return (
              <p>
                Ponechá se záznam <strong>{keep ? `${keep.lastName} ${keep.firstName}` : ''}</strong>. Choreografie ostatních
                záznamů se převedou na něj a duplicitní záznamy se smažou.
              </p>
            );
          })()}
        </ConfirmDialog>
      )}
    </div>
  );
};

export default DancersList;
